import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiFunctionsEnum } from 'constants/api.constants';
import { mapHyperMonitoringManualDocumentationDetails } from 'sections/HyperMonitoring/mapHyperMonitoringManualDocumentationDetails';
import ApiService from './api.service';
import { buildPaginationQueryParams, setErrorMessage, type IPaginationParams } from 'utils/index';
import { setError } from 'state/reducers/hyper-monitoring.reducer';
import type { HyperMonitoringOperationRecord } from 'types/hyper-monitoring.types';

/** ApiService throws `error.response` (see http/utils handleError), so prefer `error.data` / `error.status`. */
const getThunkErrorInfo = (error: any) => ({
	errorNumber: error?.data?.error_number ?? error?.response?.data?.error_number,
	statusCode: error?.status ?? error?.response?.status,
});

export const getHyperMonitoringAnalytics = createAsyncThunk(
	'hyperMonitoring/getAnalytics',
	async (_, { rejectWithValue }) => {
		try {
			const response = await ApiService.getInstance().execute(
				apiFunctionsEnum.GET_HYPER_MONITORING_ANALYTICS,
				{}
			);
			return response.data;
		} catch (error: any) {
			setErrorMessage(setError, getThunkErrorInfo(error));
			return rejectWithValue(error);
		}
	}
);

export const getHyperMonitoringOperations = createAsyncThunk(
	'hyperMonitoring/getOperations',
	async (params: IPaginationParams, { rejectWithValue }) => {
		try {
			const queryParams = buildPaginationQueryParams(params);
			const response = await ApiService.getInstance().execute(apiFunctionsEnum.GET_HYPER_MONITORING_OPERATIONS, {
				status: 'dead_letter',
				...queryParams,
			});
			return response.data;
		} catch (error: any) {
			setErrorMessage(setError, getThunkErrorInfo(error));
			return rejectWithValue(error);
		}
	}
);

/** Manual-doc modal: existing GET /beneficiary/:beneficiaryId (no new Hyper details API). */
export const getHyperMonitoringBeneficiaryDetails = createAsyncThunk(
	'hyperMonitoring/getBeneficiaryDetails',
	async (row: HyperMonitoringOperationRecord, { rejectWithValue }) => {
		try {
			const response = await ApiService.getInstance().execute(apiFunctionsEnum.GET_BENEFICIARY, {
				beneficiaryId: row.entityId,
			});
			return mapHyperMonitoringManualDocumentationDetails(row, response.data.beneficiary);
		} catch (error: any) {
			setErrorMessage(setError, getThunkErrorInfo(error));
			return rejectWithValue(error);
		}
	}
);

export const resendHyperMonitoringOperation = createAsyncThunk(
	'hyperMonitoring/resendOperation',
	async (operationId: string, { rejectWithValue }) => {
		try {
			const response = await ApiService.getInstance().execute(
				apiFunctionsEnum.RESEND_HYPER_MONITORING_OPERATION,
				{
					operationId,
				}
			);
			return { operationId, data: response.data };
		} catch (error: any) {
			setErrorMessage(setError, getThunkErrorInfo(error));
			return rejectWithValue(error);
		}
	}
);

export const markHyperMonitoringOperationDocumented = createAsyncThunk(
	'hyperMonitoring/markOperationDocumented',
	async (operationId: string, { rejectWithValue }) => {
		try {
			const response = await ApiService.getInstance().execute(
				apiFunctionsEnum.MARK_HYPER_MONITORING_OPERATION_DOCUMENTED,
				{ operationId }
			);
			return { operationId, data: response.data };
		} catch (error: any) {
			setErrorMessage(setError, getThunkErrorInfo(error));
			return rejectWithValue(error);
		}
	}
);
